import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Plus, Edit, Trash2, X, AlertCircle, Loader } from 'lucide-react';
import { useToast } from "../components/ui/toast-provider";

// Servicios
import { RolService } from "../services/puestoService";
import { EmpleadoService } from "../services/empleadoService";
import { UsuarioService } from "../services/usuarioService";

// Tipos de Response
import { RolResponse } from "../types/puesto";
import { EmpleadoResponse } from "../types/empleado";
import { UsuarioResponse } from "../types/user";

export default function CrearUsuario({ isOpen, onGuardado, onClose, idEditando }: { isOpen: boolean; onGuardado: () => void; onClose: () => void; idEditando: number }) {

    const [isLoading, setIsLoading] = useState(true);
    const [empleados, setEmpleados] = useState<EmpleadoResponse[]>([]);
    const [roles, setRoles] = useState<RolResponse[]>([]);
    const [error, setError] = useState("");
    const [editando, setEditando] = useState(false);

    const { addToast } = useToast();

    const [formulario, setFormulario] = useState({
        usuario: '',
        pass: '',
        idEmpleado: '',
        idRol: '',
        activo: true
    });

    const resetFormulario = () => {
        setFormulario({
        usuario: '',
        pass: '',
        idEmpleado: '',
        idRol: '',
        activo: true
        });
    };

    const cargarEmpleados = async () => {
        try {
            setIsLoading(true);
            const response = await EmpleadoService.getSinUsuario();
            setEmpleados(response.data);
        } catch (err) {
            console.error("Error al cargar empleados: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const cargarConEmpleado = async () => {
        try {
            setIsLoading(true);
            const response = await EmpleadoService.getAll(true);
            setEmpleados(response.data);
        } catch (err) {
            console.error("Error al cargar empleados: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const cargarRoles = async () => {
        try {
            setIsLoading(true);
            const response = await RolService.getAll();
            setRoles(response.data);
        } catch (err) {
            console.error("Error al cargar roles: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const cargarUsuario = async (id: number) => {
        try {
            setIsLoading(true);
            const response = await UsuarioService.getById(id);
            const usuario = response.data;
            setFormulario({
                usuario: usuario.usuario,
                pass: '', // No se carga la contraseña por seguridad
                idEmpleado: usuario.idEmpleado,
                idRol: usuario.idRol,
                activo: usuario.activo
            });
        } catch (err) {
            console.error("Error al cargar usuario: " + err);
        } finally {
            setIsLoading(false);
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
        const payload: UsuarioResponse = {
        usuario: formulario.usuario,
        idEmpleado: Number(formulario.idEmpleado),
        idRol: Number(formulario.idRol),
        activo: formulario.activo
        };

        // SOLO si escribió password
        if (formulario.pass.trim() !== "") {
            payload.pass = formulario.pass;
        }

        if (editando) {
            await UsuarioService.actualizar(idEditando, payload);
        } else {
            await UsuarioService.crear(payload);
        }

        addToast(editando ? "Usuario actualizado exitosamente" : "Usuario creado exitosamente");
        resetFormulario();
        onGuardado();
    } catch (err:any) {
        setError(
        err?.response?.data?.message ||
        "Error inesperado al guardar usuario"
        );
    }
    };

    useEffect(() => {
        if(isOpen) {
            resetFormulario();
            if (idEditando !== 0) {
                cargarUsuario(idEditando);
                cargarConEmpleado();
            }else {
                cargarEmpleados();
            }
            setEditando(idEditando !== 0);
            cargarRoles();
            setError("");
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }
    return (
        <div className="fixed inset-0 bg-opacity-50 flex items-center justify-center z-50">
            <div className="rounded-2xl w-full max-w-2xl shadow-lg relative max-h-[90vh] overflow-y-auto scrollbar-thin">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle>{editando ? 'Editar Usuario' : 'Crear Usuario'}</CardTitle>
                        <button
                        onClick={onClose}
                        className="p-2 hover:bg-destructive/10 rounded-lg transition-colors"
                        >
                        <X className="w-5 h-5 text-destructive" />
                        </button>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input
                            label="Usuario"
                            type="text"
                            value={formulario.usuario}
                            onChange={(e) =>
                                setFormulario({ ...formulario, usuario: e.target.value })
                            }
                            placeholder="Ingrese el nombres de usuario"
                            required
                            />
                            <Input
                            label="Contraseña"
                            type="password"
                            value={formulario.pass}
                            onChange={(e) =>
                                setFormulario({ ...formulario, pass: e.target.value })
                            }
                            placeholder={editando ? 'Dejar en blanco para no editar' : 'Ingrese la contraseña'}
                            required={!editando}
                            />
                            <Select
                            label="Empleado"
                            value={formulario.idEmpleado}
                            onChange={(e) =>
                                setFormulario({ ...formulario, idEmpleado: e.target.value })
                            }
                            options={empleados.map((e) => ({ value: (e.idEmpleado!).toString(), label: e.nombreCompleto || `${e.nombres} ${e.apellidos}` }))}
                            required
                            disabled={editando} // No se puede cambiar el empleado al editar
                            />
                            <Select
                            label="Rol"
                            value={formulario.idRol}
                            onChange={(e) =>
                                setFormulario({ ...formulario, idRol: e.target.value })
                            }
                            options={roles.map((r) => ({ value: (r.idRol).toString(), label: r.rol }))}
                            required
                            />
                        </div>
                        <div className = "flex justify-center">
                        {error && (
                            <span className="text-destructive text-center text-sm">{error}</span>
                        )}
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" variant="primary">
                            {editando ? 'Editar Usuario' : 'Crear Usuario'}
                            </Button>
                            <Button type="button" variant="ghost" onClick={onClose}>
                            Cancelar
                            </Button>
                        </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    )

}